<template lang="pug">
	.newMeasurementContainer
		form(@submit.prevent="recordFormSubmitted")
			.modal-card: .modal-card-body
				//- DEMO
				div(style="margin-bottom: 2rem;")
					p(style="color: red;")
						span DEMO: 
						span: strong: a(@click.prevent="simulateDevice", style="color: red;") Click here to simulate a temperature reading from the physical device
					p If it does not work, you may need to refresh the page.
				//- Organization Dropdown
				b-field(label="Department", :type="{'is-danger': orgErrors.length }", :message="orgErrors")
					b-select(v-model="record.org", :disabled="userOrgs.length < 2")
						option(v-for="org in userOrgs", :key="org", :value="org") {{ org }}
				br
				//- Facial Recognition Instructions
				template(v-if="useFaceRecognition")
					p: strong {{ calibratedTemperature ? ((faceMatchData.handDetected) ? "Recording..." : "Raise your hand to record the measurement.") : "Please take your temperature." }}

				//- Temperature Info Section
				.socketContainer
					div(v-if="!calibratedTemperature")
						p Waiting for temperature info...
					div(v-else)
						//- https://momentjs.com/docs/#/displaying/
						p Time: {{ moment(record.timestamp).tz("Pacific/Honolulu").format("MM/DD/YY, hh:mm A zz") }}
						p.has-text-danger Temp: {{ calibratedTemperature }}&#8457;
				p.spacer

				//- Face Recognition Section
				template(v-if="useFaceRecognition")
					.columns
						.column
							//- Camera Component
							Camera(:org="record.org", @cameraSetEmployeeTag="cameraSetEmployeeTag")
						.column
							p ID: {{ record.employeeTag }}
							p Name: {{ faceMatchData.name }}
							p Match: {{ faceMatchConfidence }}%

				//- Not using face recognition, so manually enter employee tag ("ID")
				template(v-else)
					.dropdownSection
						b-field(label="ID", :type="{'is-danger': tagErrors.length }", :message="tagErrors")
							b-autocomplete(v-model="record.employeeTag", :data="filteredEmployeeList", field="tag", placeholder="", icon="magnify", clearable, @select="(option) => selectedDropdownEmployee = option", :open-on-focus="true", :keep-first="true", :disabled="isLoading")
								template(slot="empty") No results found
						p {{ selectedEmployee.name }}
				p.spacer
				//- Footer Buttons
				.level
					.level-left
						.level-item: input.button.is-primary#submitButton(type="submit", name="submit", value="Record", :disabled="isLoading || !calibratedTemperature || !record.employeeTag")
						.level-item: button.button.is-light(@click.prevent="clearForm", :disabled="isLoading") Clear
					.level-right
						.level-item: button.button.is-dark(@click.prevent="$parent.close()") Close
</template>

<style lang="scss" scoped>
	.modal-card-body {
		border-radius: 6px;

		.dropdownSection {
			min-height: 220px;
		}
	}
</style>

<script>
	import { mapState, mapGetters } from "vuex";
	import moment from "moment";
	import "moment-timezone";

	export default {
		components: {
			Camera: () => import("@/components/Camera.vue")
		},
		props: ["useFaceRecognition"],
		data() {
			return {
				record: this.generateBlankRecord(),
				isLoading: false,
				selectedDropdownEmployee: null,
				faceMatchData: {},
				errors: [],
				serverErrors: []
			};
		},
		computed: {
			...mapState(["measurementData", "user"]),
			...mapGetters(["userOrgs", "employees"]),
			// Calibrated temperature
			calibratedTemperature() {
				// From the last 4 temperatures, take the average of the first 2 (the last 2 are probably wrong from moving away from the sensor)
				// if (this.measurementData.length < 4) {
				// 	return null;
				// }
				// let calibratedTemp = 0.0;
				// for (var i = this.measurementData.length - 3; i >= this.measurementData.length - 4; i--) {
				// 	calibratedTemp += this.measurementData[i].oTemp;
				// }
				// calibratedTemp = calibratedTemp / 2;
				// return +calibratedTemp.toFixed(2); // to 2 decimal places, used to fix "97.5000000001"

				// DEMO
				if (this.measurementData.length < 1) {
					return null;
				}
				return +this.measurementData[this.measurementData.length - 1].oTemp.toFixed(2);
			},
			// Face recognition confidence
			faceMatchConfidence() {
				if (this.faceMatchData && !isNaN(this.faceMatchData.azureConfidence))
					return (this.faceMatchData.azureConfidence * 100).toFixed(0);
				return 0;
			},
			// Filter employees in dropdown by selected organization AND based on user input
			filteredEmployeeList() {
				let allEmployees = JSON.parse(JSON.stringify(this.employees));
				return allEmployees
					.filter((option) => option.org === this.record.org)
					.filter((option) => {
						return (
							option.tag
								.toString()
								.toLowerCase()
								.indexOf(this.record.employeeTag.toLowerCase()) === 0
						);
					});
			},
			// The selected employee in the dropdown
			selectedEmployee() {
				return this.selectedDropdownEmployee
					? this.user.employees.find((em) => em.tag === this.selectedDropdownEmployee.tag)
					: { name: "", notes: "" };
			},
			orgErrors() {
				return this.errors.filter((el) => el.toLowerCase().indexOf("org") > -1).join(" ");
			},
			tagErrors() {
				return this.errors.filter((el) => el.toLowerCase().indexOf("id") > -1).join(" ");
			},
			internalErrors() {
				return this.errors.filter((el) => el.toLowerCase().indexOf("unknown error") > -1).join(" ");
			}
		},
		methods: {
			// Date and time formatting library
			moment() {
				return moment();
			},
			// A blank record object that gets filled with temp and employee data, then sent to server
			generateBlankRecord() {
				return {
					timestamp: null,
					objectTemp: "",
					ambientTemp: "",
					fromDeviceID: null,
					org: this.userOrgs ? this.userOrgs[0] : "", // field set by user
					employeeTag: "", // field set by user
					employerID: this.$store.state.user._id,
					employerEmail: this.$store.state.user.email
				};
			},
			// Upon pressing Clear
			clearForm(keepOrg) {
				this.$store.dispatch("emptyMeasurementData");
				this.record = this.generateBlankRecord();
				if (keepOrg) this.record.org = keepOrg; // (keep current org status)
				this.errors = [];
				this.serverErrors = [];
			},
			// This event is fired when the camera found a matched employee
			cameraSetEmployeeTag(faceMatchData) {
				this.faceMatchData = faceMatchData; // used for displaying employee name and Azure confidence score
				this.record.employeeTag = faceMatchData.tag; // set record's employee tag

				// If everything is filled out and employee raises their hand, then submit
				if (!document.getElementById("submitButton").disabled && faceMatchData.handDetected) {
					this.recordFormSubmitted();
				}
			},
			// Upon pressing Record
			recordFormSubmitted() {
				// Prevent user input
				this.isLoading = true;
				const loadingComponent = this.$buefy.loading.open();

				// Vuex action to send to database
				this.$store
					.dispatch("createNewRecord", this.record)
					.then(() => {
						// param: newRecord
						// Show Success Notification
						this.$buefy.snackbar.open({
							duration: 3000,
							message: "Recorded successfully.",
							type: "is-success",
							position: "is-top-right"
						});

						// Push to router
						// this.$router.push({
						// 	name: "home",
						// 	// params: { urlName: newRecord.urlName }
						// });

						// Clear record for next measurement (keep current org status)
						this.clearForm(this.record.org);
						loadingComponent.close();
						this.isLoading = false;
					})
					.catch((errData) => {
						console.log(errData);
						// Errors, i.e. user did not fill out all fields
						this.errors = errData.userErrors || [];
						this.serverErrors = errData.serverErrors;

						if (this.internalErrors.length) {
							this.$buefy.snackbar.open({
								duration: 3000,
								message: this.internalErrors,
								type: "is-danger",
								position: "is-top-right"
							});
						}
						loadingComponent.close();
						this.isLoading = false;
					});
			},
			simulateDevice() {
				this.$store.dispatch("demoSimulateDevice");
			}
		},
		mounted() {
			// Listen for temperature data from server via Socket.io, on the room that matches employer ID
			this.$socket.client.on(this.user.socketID, (measurementData) => {
				this.record.timestamp = new Date();
				this.record.ambientTemp = measurementData.aTemp;
				this.record.fromDeviceID = measurementData.deviceID;
				this.$store.dispatch("updateMeasurementData", measurementData);

				// Set record's object temp to the calibrated temperature if there is one
				this.record.objectTemp = this.calibratedTemperature || measurementData.oTemp;
			});

			// Default the selected organization to the first in the dropdown of organizations
			this.record.org = this.userOrgs[0];
		},
		beforeDestroy() {
			this.$store.dispatch("emptyMeasurementData");
			this.$socket.client.off(this.user.socketID);
		}
	};
</script>
